import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Flex,
  Heading,
  Text,
  Spinner,
  useToast,
  useDisclosure,
  Button,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import useThemeColors from "../../../../../hooks/useThemeColors";
import SidePanel from "../../../../../components/sidepanelNew/SidePanel";
import {
  primary,
  primaryAlt,
  textSecondary,
  white,
} from "../../../../../utils/colors";
import { columns } from "./ClinicUserListColumns"; // Adjust the path according to your file structure
import Table from "../../../../../components/shared/Table";
import { useDispatch, useSelector } from "react-redux";
import {
  listUsersAction,
  deleteUserAction,
} from "../../../../../actions/userActions";
import useDebounce from "../../../../../hooks/useDebounce";
import TableContainerWindowed from "../../../../../components/shared/Table/TableContainerWindowed";
import { useNavigate, useParams } from "react-router-dom";
import useIsMobile from "../../../../../hooks/useIsMobile";
import {
  USER_DELETE_RESET,
  USER_LIST_RESET,
} from "../../../../../constants/userConstants";
import ConfirmDeleteModal from "../../../../../components/modals/ConfirmDeleteModal";
import { inviteUserToTenantAction } from "../../../../../actions/tenantActions";
import { PERMISSION_PRESETS } from "./permissionPresets"; // Adjust the path according to your file structure
import InviteUser from "./InviteUser";
import {
  TENANT_INVITE_USER_RESET,
  TENANT_UPDATE_TENANT_USER_PERMISSIONS_RESET,
} from "../../../../../constants/tenantConstants";
import EditUser from "./EditUser";

const ClinicUsersList = ({ tenant, userInfo }) => {
  // GENERAL
  const { slug } = useParams();
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  // COLORS
  const { whiteMode, backgroundLightMode, textPrimaryMode, newBorderMode } =
    useThemeColors();

  // STATE
  // const [showButtons, setShowButtons] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [userId, setUserId] = useState(null);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [editModalOpen, setEditModalOpen] = useState(false);

  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("employee");
  const [invitePermissions, setInvitePermissions] = useState(
    PERMISSION_PRESETS.employee
  );

  // LANGUAGE
  const storedLanguage = localStorage.getItem("language");
  const initialLanguage = storedLanguage || "lt"; // Default to 'en'
  const [currentLanguage, setCurrentLanguage] = useState(initialLanguage);

  // REDUX
  const dispatch = useDispatch();

  const userList = useSelector((state) => state.userList);
  const { loading, error, success, users, pages } = userList;

  const userDelete = useSelector((state) => state.userDelete);
  const {
    loading: loadingDelete,
    error: errorDelete,
    success: successDelete,
  } = userDelete;

  const tenantInviteUserToTenant = useSelector(
    (state) => state.tenantInviteUserToTenant
  );
  const {
    loading: loadingInvite,
    error: errorInvite,
    success: successInvite,
    message,
  } = tenantInviteUserToTenant;

  const tenantUpdateTenantUserPermissions = useSelector(
    (state) => state.tenantUpdateTenantUserPermissions
  );
  const {
    loading: loadingUpdatePermissions,
    error: errorUpdatePermissions,
    success: successUpdatePermissions,
  } = tenantUpdateTenantUserPermissions;

  // PAGINATION
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const pageSizes = [4, 10, 25, 100];

  // FILTERING
  const [resetFilter, setResetFilter] = useState(false);
  const [isFilterActive, setIsFilterActive] = useState(false);
  const { isOpenFilter, onOpenFilter, onCloseFilter } = useDisclosure();

  const filterConfig = {
    employees: true,
  };

  const [filterParams, setFilterParams] = useState({
    status: [],
    startDate: "",
    endDate: "",
    profession: [],
  });

  const deleteHandler = (id) => {
    dispatch(deleteUserAction(slug, id));
  };

  const handleInvite = async () => {
    await dispatch(
      inviteUserToTenantAction(
        slug,
        inviteEmail,
        inviteRole,
        invitePermissions,
        currentLanguage
      )
    );

    console.log("Invite action dispatched", {
      slug,
      inviteEmail,
      inviteRole,
      invitePermissions,
      currentLanguage,
    });
  };

  const handleFilterApply = (filters) => {
    setFilterParams(filters);
    setPage(1);
    setIsFilterActive(true);
    setResetFilter(false); // Reset the reset state after applying the filter
    localStorage.setItem("filterSettings_employees", JSON.stringify(filters));

    dispatch(
      listUsersAction(
        slug,
        debouncedSearchTerm,
        1,
        pageSize,
        sortField,
        sortOrder
      )
    );
  };

  const handleResetFilter = () => {
    const resetState = {
      startDate: "",
      endDate: "",
      status: [],
      profession: [],
    };
    setFilterParams(resetState);
    localStorage.setItem(
      "filterSettings_employees",
      JSON.stringify(resetState)
    ); // Reset localStorage

    console.log("Resetting filters", resetState);
    console.log(
      "Resetting filters",
      localStorage.getItem("filterSettings_employees")
    );
    setResetFilter(true);
    setIsFilterActive(false);
    dispatch(
      listUsersAction(
        slug,
        debouncedSearchTerm,
        1,
        pageSize,
        sortField,
        sortOrder
      )
    );
  };

  //SORTING
  const [sortField, setSortField] = useState("userID");
  const [sortOrder, setSortOrder] = useState("desc");

  const handleSortChange = useCallback(
    (column) => {
      // Toggle sort order if the same column is clicked, or default to ascending
      const newSortOrder =
        sortField === column && sortOrder === "asc" ? "desc" : "asc";

      // Update sort field and order
      setSortField(column);
      setSortOrder(newSortOrder);

      // setPage(1);
    },
    [sortField, sortOrder]
  );

  // SEARCH
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 600); // Use debounced value
  const normalizeText = (text) => {
    return text.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  };
  const onChangeSearchTitle = (e) => {
    const normalizedText = normalizeText(e.target.value);
    setSearchTerm(normalizedText);
    setPage(1); // Reset page to 1 after search
  };

  // HANDLERS

  // SIDEPANEL HANDLERS
  const openEditSidePanelModal = () => {
    setEditModalOpen(true);
    document.body.classList.add("no-scroll");
  };

  const closeEditSidePanelModal = () => {
    setEditModalOpen(false);
    document.body.classList.remove("no-scroll");
  };

  // USE EFFECT

  // USEEFFECT
  useEffect(() => {
    if (debouncedSearchTerm.length >= 3 || debouncedSearchTerm === "") {
      setIsFetching(true);
      dispatch(
        listUsersAction(
          slug,
          debouncedSearchTerm,
          1,
          pageSize,
          sortField,
          sortOrder
        )
      ).then(() => setIsFetching(false));
    }
  }, [
    slug,
    debouncedSearchTerm,
    page,
    pageSize,
    sortField,
    sortOrder,
    successInvite,
    successUpdatePermissions,
    successDelete,

    // successCreate,
    // successDelete,
  ]);

  useEffect(() => {
    if (error) {
      toast({
        title: t("toasts.userListError"),
        status: "error",
        duration: 3000,

        isClosable: true,
      });
    }

    dispatch({ type: USER_LIST_RESET });
  }, [error]);

  useEffect(() => {
    if (successInvite) {
      toast({
        title: t("toasts.userInviteSuccess"),
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      // Reset invite state
      setInviteEmail("");
      setInviteRole("employee");
      setInvitePermissions(PERMISSION_PRESETS.employee);
      onClose();
    }
    if (errorInvite) {
      toast({
        title: t("toasts.userInviteError"),
        description: errorInvite,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }

    // Reset invite state after success or error
    dispatch({ type: TENANT_INVITE_USER_RESET });
  }, [successInvite, errorInvite]);

  useEffect(() => {
    if (successUpdatePermissions) {
      toast({
        title: t("toasts.userPermissionsUpdateSuccess"),
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    }
    if (errorUpdatePermissions) {
      toast({
        title: t("toasts.userPermissionsUpdateError"),
        description: errorUpdatePermissions,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }

    closeEditSidePanelModal();
    // Reset update permissions state after success or error
    dispatch({ type: TENANT_UPDATE_TENANT_USER_PERMISSIONS_RESET });
  }, [successUpdatePermissions, errorUpdatePermissions]);

  useEffect(() => {
    if (successDelete) {
      toast({
        title: t("toasts.userDeleteSuccess"),
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      setUserId(null);
      setShowDeleteConfirmation(false);
    }
    if (errorDelete) {
      toast({
        title: t("toasts.userDeleteError"),
        description: errorDelete,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }

    dispatch({ type: USER_DELETE_RESET });
  }, [successDelete, errorDelete]);

  const tenantRegistrationEmail = tenant && tenant?.registrationEmail;
  const loggedInUserEmail = userInfo && userInfo.email;

  // LOGS
  // console.log("tenant", tenant);
  // console.log("showButtons", showButtons);
  // console.log("clinicName", clinicName);
  // console.log("registrationEmail", registrationEmail);
  // console.log("supportEmail", supportEmail);
  // console.log("phone", phone);
  // console.log("address", address);

  // console.log("users", users);
  // console.log("message", message);

  // console.log("inviteEmail", inviteEmail);
  // console.log("inviteRole", inviteRole);
  // console.log("invitePermissions", invitePermissions);

  // console.log("userId", userId);

  return (
    <>
      {/* SIDEPANELS  */}
      <SidePanel
        open={isOpen}
        close={onClose}
        title={t("user.inviteUser")}
        noBorder={true}
      >
        <Flex w="100%" bg={whiteMode} minW="800px" maxW="800px">
          <InviteUser
            close={onClose}
            email={inviteEmail}
            setEmail={setInviteEmail}
            role={inviteRole}
            setRole={setInviteRole}
            onInvite={handleInvite}
            loading={loadingInvite}
            permissions={invitePermissions}
            setPermissions={setInvitePermissions}
          />
        </Flex>
      </SidePanel>

      <SidePanel
        open={editModalOpen}
        close={closeEditSidePanelModal}
        title={t("user.editUser")}
        noBorder={true}
      >
        <Flex w="100%" bg={whiteMode} minW="800px" maxW="800px">
          <EditUser
            slug={slug}
            loading={loadingUpdatePermissions}
            close={closeEditSidePanelModal}
            user={users && users.find((user) => user._id === userId)}
          />
        </Flex>
      </SidePanel>

      {/* MODALS START */}
      {showDeleteConfirmation && (
        <ConfirmDeleteModal
          hasAccessToDelete={true}
          deleteTitle={t("user.deleteUser")}
          deleteName={users && users.find((user) => user._id === userId)?.email}
          warningLabel={t("user.deleteWarning")}
          isOpen={showDeleteConfirmation}
          onClose={() => setShowDeleteConfirmation(false)}
          confirmAction={() => {
            deleteHandler(userId);
            setShowDeleteConfirmation(false);
          }}
        />
      )}
      {/* 
      <InviteUserModal
        isOpen={isOpen}
        onClose={onClose}
        email={inviteEmail}
        setEmail={setInviteEmail}
        role={inviteRole}
        setRole={setInviteRole}
        onInvite={handleInvite}
        loading={loadingInvite}
        permissions={invitePermissions}
        setPermissions={setInvitePermissions}
      /> */}
      {/* MODALS END */}

      <Box
        w="100%"
        p="3rem 0rem 3rem"
        borderBottom={`1px solid ${newBorderMode}`}
      >
        <Flex w="100%" justify="space-between" gap="2rem">
          {/* LEFT START */}
          <Flex direction="column" gap={2} w="40%">
            <Heading fontSize="1rem" fontWeight="600">
              {t("clinicSettings.tenantUsers")}
            </Heading>
            <Text
              color={textSecondary}
              fontSize="sm"
              fontWeight="500"
              maxW="90%"
            >
              {t("clinicSettings.tenantUsersText")}
            </Text>
          </Flex>
          {/* LEFT END */}

          {/* RIGHT START */}
          <Flex
            w="800px"
            direction="column"
            justify="flex-start"
            align="flex-start"
          >
            <Flex
              direction="column"
              w="100%"
              bg={backgroundLightMode}
              borderRadius="1rem"
              p="1.25rem 1.5rem 1.25rem"
            >
              <Flex
                direction="column"
                w="100%"
                bg={whiteMode}
                borderRadius="1rem"
                p="1rem 0rem 0rem"
              >
                <Box p="0 1.5rem 1rem">
                  <Flex w="100%" align="center" justify="space-between">
                    <Heading
                      fontSize="md"
                      fontWeight="600"
                      color={textPrimaryMode}
                      lineHeight="1"
                    >
                      {t("user.users")}
                    </Heading>
                    <Button
                      size="md"
                      fontSize="sm"
                      borderRadius="10rem"
                      bg={textPrimaryMode}
                      color={white}
                      px="1.5rem"
                      onClick={onOpen}
                      _hover={{ bg: primaryAlt }}
                    >
                      {t("user.inviteUser")}
                    </Button>
                  </Flex>
                </Box>

                {!loading ? (
                  <>
                    <TableContainerWindowed
                      columns={columns(
                        t,
                        isMobile,
                        textPrimaryMode,
                        whiteMode,
                        setUserId,
                        setShowDeleteConfirmation,
                        openEditSidePanelModal,
                        tenantRegistrationEmail,
                        loggedInUserEmail
                      )}
                      data={users}
                      loading={loading}
                      error={error}
                      onRowClick={(userId) => {
                        setUserId(userId);
                        openEditSidePanelModal();
                      }}
                      noItemsTitle={t("noItems.noEmployees")}
                      noItemsDescription={t("noItems.noEmployeesText")}
                      noItemsButtonLabel={t("noItems.noEmployeesButton")}
                      // noItemsButtonHandler={openCreateEmployeeSidePanelModal}
                      sortField={sortField}
                      sortOrder={sortOrder}
                      handleSortChange={handleSortChange}
                    />
                    <Flex p="0rem 0 0rem" w="100%">
                      <Table.Pagination
                        isSmall
                        loading={loading}
                        page={page}
                        pages={pages}
                        pageSize={pageSize}
                        pageSizes={pageSizes}
                        navigate={navigate}
                        setPage={setPage}
                        setPageSize={setPageSize}
                        totalCount={users && users.length}
                      />
                    </Flex>
                  </>
                ) : (
                  <Flex
                    w="100%"
                    justify="center"
                    align="flex-start"
                    p="3rem 2rem"
                    minH="800px"
                  >
                    <Spinner size="lg" color={primary} />
                  </Flex>
                )}
              </Flex>
            </Flex>
          </Flex>
          {/* RIGHT END */}
        </Flex>

        {/* BUTTONS */}
      </Box>
    </>
  );
};

export default ClinicUsersList;
